import React, { useState, useEffect } from "react";
import moment from "moment";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import "../../styles/detail.css";

const UserTransactions = ({ user, userId, setUser }) => {
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTransactions = () => {
      const userTransactions = user?.transactions || [];
      const sorted = [...userTransactions]
        .map((t, idx) => ({ ...t, originalIndex: idx }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setTransactions(sorted);

      const balance = userTransactions.reduce(
        (sum, t) => (t.type === "credit" ? sum + t.amount : sum - t.amount),
        0,
      );
      const credit = userTransactions
        .filter((t) => t.type === "credit")
        .reduce((sum, t) => sum + t.amount, 0);
      const debit = userTransactions
        .filter((t) => t.type === "debit")
        .reduce((sum, t) => sum + t.amount, 0);

      setBalance(balance);
      setTotalCredit(credit);
      setTotalDebit(debit);
      setLoading(false);
    };
    fetchTransactions();
  }, [user]);

  const handleViewClick = (transaction) => {
    setSelectedTransaction({ ...transaction });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedTransaction((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const updatedTransactions = (user?.transactions || []).map((t, idx) => {
        if (idx === selectedTransaction.originalIndex) {
          const { originalIndex, ...rest } = selectedTransaction;
          return rest;
        }
        return t;
      });
      await updateDoc(doc(db, "users", userId), {
        transactions: updatedTransactions,
      });
      setUser((prev) => ({ ...prev, transactions: updatedTransactions }));
      setSuccessMessage("Transaction updated");
      setTimeout(() => {
        setSuccessMessage("");
        setSelectedTransaction(null);
      }, 2000);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    setError("");
    try {
      const updatedTransactions = (user?.transactions || []).filter(
        (_, idx) => idx !== selectedTransaction.originalIndex,
      );
      await updateDoc(doc(db, "users", userId), {
        transactions: updatedTransactions,
      });
      setUser((prev) => ({ ...prev, transactions: updatedTransactions }));
      setSuccessMessage("Transaction deleted");
      setTimeout(() => {
        setSuccessMessage("");
        setSelectedTransaction(null);
      }, 2000);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#aaa" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="profile-grid detail-container">
      <div className="detail-full-width">
        <div className="detail-info-box">
          <div className="detail-info-stat">
            <span className="detail-stat-label">Total</span>
            <span className="detail-stat-value">{transactions.length}</span>
          </div>
          <div className="detail-info-stat">
            <span className="detail-stat-label">Debit</span>
            <span className="detail-stat-value debit">
              ${totalDebit.toLocaleString()}
            </span>
          </div>
          <div className="detail-info-stat">
            <span className="detail-stat-label">Credit</span>
            <span className="detail-stat-value credit">
              ${totalCredit.toLocaleString()}
            </span>
          </div>
          <div className="detail-info-stat">
            <span className="detail-stat-label">Balance</span>
            <span
              className={`detail-stat-value ${balance >= 0 ? "credit" : "debit"}`}
            >
              ${balance.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {transactions.length === 0 ? (
        <div className="detail-empty">
          <i className="fas fa-receipt"></i>
          <p>No transactions found</p>
        </div>
      ) : (
        <div className="detail-table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th>Amount</th>
                <th className="noShow">Description</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                <tr key={index} onClick={() => handleViewClick(transaction)}>
                  <td>
                    <i
                      className={`fas fa-circle-${transaction.type === "credit" ? "plus" : "minus"} detail-table-icon ${transaction.type}`}
                    ></i>
                    <span className={`detail-table-text ${transaction.type}`}>
                      ${transaction.amount?.toLocaleString()}
                    </span>
                  </td>
                  <td className="noShow detail-table-text muted">
                    {transaction.description || "N/A"}
                  </td>
                  <td className="detail-table-text muted">
                    {moment(transaction.date).format("MMM D, YYYY")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedTransaction && (
        <>
          <div
            className="detail-modal-overlay"
            onClick={() => setSelectedTransaction(null)}
          />
          <div className="detail-modal">
            <div className="detail-modal-header">
              <div className="detail-modal-header-row">
                <h3 className="detail-modal-title">Transaction Details</h3>
                <span
                  className="detail-modal-close"
                  onClick={() => setSelectedTransaction(null)}
                >
                  &times;
                </span>
              </div>
              <div className="detail-modal-info">
                <div>
                  <label className="profile-label">Amount</label>
                  <div
                    className={`profile-value detail-table-text ${selectedTransaction.type}`}
                  >
                    ${selectedTransaction.amount?.toLocaleString()}
                  </div>
                </div>
                <div>
                  <label className="profile-label">Date</label>
                  <div className="profile-value">
                    {moment(selectedTransaction.date).format("MMM D, YYYY")}
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-modal-content">
              <div>
                <label className="profile-label">
                  Description
                  <input
                    type="text"
                    name="description"
                    className="profile-input"
                    placeholder="Enter description"
                    value={selectedTransaction.description || ""}
                    onChange={handleEditChange}
                  />
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Type
                  <select
                    name="type"
                    className="profile-input"
                    value={selectedTransaction.type}
                    onChange={handleEditChange}
                  >
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Account
                  <select
                    name="account"
                    className="profile-input"
                    value={selectedTransaction.account || ""}
                    onChange={handleEditChange}
                  >
                    <option value="">Select Account</option>
                    <option value="Savings">Savings</option>
                    <option value="Investments">Investments</option>
                    <option value="Trading">Trading</option>
                    <option value="Loans">Loans</option>
                  </select>
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Beneficiary
                  <input
                    type="text"
                    name="beneficiary"
                    className="profile-input"
                    placeholder="Enter beneficiary name"
                    value={selectedTransaction.beneficiary || ""}
                    onChange={handleEditChange}
                  />
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Beneficiary Account
                  <input
                    type="text"
                    name="beneficiaryAccount"
                    className="profile-input"
                    placeholder="Enter beneficiary account"
                    value={selectedTransaction.beneficiaryAccount || ""}
                    onChange={handleEditChange}
                  />
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Sender
                  <input
                    type="text"
                    name="sender"
                    className="profile-input"
                    placeholder="Enter sender name"
                    value={selectedTransaction.sender || ""}
                    onChange={handleEditChange}
                  />
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Explore Link
                  <input
                    type="text"
                    name="explore"
                    className="profile-input"
                    placeholder="eg. https://explore.com"
                    value={selectedTransaction.explore || ""}
                    onChange={handleEditChange}
                  />
                </label>
              </div>

              <div>
                <label className="profile-label">
                  Status
                  <select
                    name="status"
                    className="profile-input"
                    value={selectedTransaction.status || ""}
                    onChange={handleEditChange}
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="complete">Completed</option>
                    <option value="processing">Processing</option>
                  </select>
                </label>
              </div>

              {successMessage && (
                <div className="detail-success">{successMessage}</div>
              )}
              {error && <div className="detail-error">{error}</div>}
            </div>

            <div className="detail-modal-footer">
              <button
                onClick={handleSave}
                className="profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleDelete}
                className="detail-btn-delete"
                disabled={isSaving}
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="detail-btn-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserTransactions;
